import type { PapelNoCliente } from "@base/contracts";
import {
  Brain,
  Building2,
  FileText,
  LayoutDashboard,
  Settings2,
  ShieldCheck,
  UserCog,
  KeyRound,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { PaginaDeClientes } from "./modules/clientes/presentation/pagina-de-clientes";
import { PaginaDeDadosDoProjeto } from "./modules/clientes/presentation/pagina-de-dados-do-projeto";
import { BotaoDeSincronizarKommo } from "./modules/conexoes/presentation/botao-de-sincronizar";
import { useClienteSelecionado } from "./modules/clientes/application/clientes.contexto";
import { PaginaDeConexoes } from "./modules/conexoes/presentation/pagina-de-conexoes";
import {
  CAMINHO_DAS_CONEXOES_DA_MONTE,
  PaginaDeConexoesDaMonte,
} from "./modules/conexoes/presentation/pagina-de-conexoes-da-monte";
import {
  CAMINHO_DO_MONITORAMENTO,
  PaginaDeMonitoramentoDeContas,
} from "./modules/conexoes/presentation/pagina-de-monitoramento-de-contas";
import { useMetaDoPeriodo } from "./modules/funil-projetado";
import {
  CAMINHO_DO_FUNIL_PROJETADO,
  PaginaDeFunilProjetado,
} from "./modules/funil-projetado/presentation/pagina-de-funil-projetado";
import { CAMINHO_DO_KOMMO, PaginaDoKommo } from "./modules/conexoes/presentation/pagina-do-kommo";
import { CAMINHO_DO_GOOGLE, PaginaDoGoogle } from "./modules/conexoes/presentation/pagina-do-google";
import { CAMINHO_DO_META, PaginaDoMeta } from "./modules/conexoes/presentation/pagina-do-meta";
import { PaginaDeDashboard } from "./modules/dashboard/presentation/pagina-de-dashboard";
import { CentralAoVivo } from "./modules/central/presentation/central-ao-vivo";
import { ContaAoVivo } from "./modules/central/presentation/conta-ao-vivo";
import { CAMINHO_DA_CONTA } from "./modules/central/presentation/pagina-da-conta";
import { PaginaDePessoas } from "./modules/clientes/presentation/pagina-de-pessoas";
import { PaginaDePerfil } from "./modules/usuarios/presentation/pagina-de-perfil";
import { PaginaDeUsuarios } from "./modules/usuarios/presentation/pagina-de-usuarios";
import type { GrupoDeNavegacao, ItemDeNavegacao } from "./shared/layout/nav-main";
import { PaginaPlanejada } from "./shared/layout/pagina-planejada";
import { navegar } from "./shared/navegacao/navegacao";

/** O que decide quem ve cada parte do menu. */
export type ContextoDoMenu = {
  readonly nomeDoUsuario: string;
  readonly equipeMonte: boolean;
  /** Papel no cliente selecionado; `null` para a equipe Monte ou sem cliente. */
  readonly papelNoCliente: PapelNoCliente | null;
  /** Nome do cliente selecionado; `null` quando nao ha nenhum. */
  readonly cliente: string | null;
  readonly clienteId: string | null;
  /** Dados da propria conta, para Meu perfil. */
  readonly conta: {
    readonly nome: string;
    readonly email: string;
    readonly telefone: string | null;
    readonly cargo: string | null;
    /** Administrador, Gestor ou Membro. */
    readonly cargoNoPainel: string;
  };
};

type Area = "projeto" | "configuracoes" | "gestao" | "monte" | "conta";

type Pagina = {
  readonly caminho: string;
  readonly titulo: string;
  readonly icone: LucideIcon;
  readonly area: Area;
  readonly renderizar: (contexto: ContextoDoMenu) => ReactNode;
  /** Subpagina: tem rota e breadcrumb, mas nao item proprio na barra lateral. */
  readonly foraDoMenu?: boolean;
  /** Regra extra alem da area (ex.: Acessos no Projeto, so equipe Monte e gestor). */
  readonly visivel?: (contexto: ContextoDoMenu) => boolean;
};

/** Quem ve cada area. O servidor confere de novo: isto e so o que mostrar. */
const PODE_VER: Readonly<Record<Area, (contexto: ContextoDoMenu) => boolean>> = {
  projeto: () => true,
  configuracoes: (c) => c.equipeMonte || c.papelNoCliente === "gestor",
  gestao: (c) => c.equipeMonte,
  monte: (c) => c.equipeMonte,
  conta: () => true,
};

/** Paginas do cliente precisam de um cliente selecionado. */
export const AREAS_DO_CLIENTE: ReadonlySet<Area> = new Set(["projeto", "configuracoes"]);

/** Visão geral e detalhe da conta: a barra lateral começa recolhida. */
export function estaNaCentral(caminho: string): boolean {
  return caminho === "/gestao/central" || caminho.startsWith("/gestao/central/");
}

const planejada =
  (titulo: string, descricao: string, previsto: readonly string[], comEsqueleto = false) =>
  (c: ContextoDoMenu) => (
    <PaginaPlanejada
      rotulo={c.cliente}
      titulo={titulo}
      descricao={descricao}
      previsto={previsto}
      comEsqueleto={comEsqueleto}
    />
  );

/**
 * Mapa do produto: toda pagina, a area dela e o que ela mostra. O menu, o
 * breadcrumb e o controle de acesso das telas saem daqui, entao nao divergem.
 *
 * Estrutura pensada para analise de resultados por cliente, seguindo o metodo
 * da Monte: Anuncios -> WhatsApp -> Agendamentos -> Indicadores.
 */
export const PAGINAS: readonly Pagina[] = [
  // --- Projeto (cliente selecionado) -------------------------------------
  {
    caminho: "/",
    titulo: "Dashboard",
    icone: LayoutDashboard,
    area: "projeto",
    // O app so renderiza pagina de cliente com um cliente selecionado.
    renderizar: (c) =>
      c.clienteId && c.cliente ? (
        <PaginaDeDashboard
          clienteId={c.clienteId}
          cliente={c.cliente}
          nomeDoUsuario={c.nomeDoUsuario}
          usarMeta={useMetaDoPeriodo}
          {...(PODE_VER.configuracoes(c) ? { aoPlanejar: () => navegar(CAMINHO_DO_FUNIL_PROJETADO) } : {})}
          acaoDeSincronizar={
            PODE_VER.configuracoes(c) ? <BotaoDeSincronizarKommo clienteId={c.clienteId} /> : null
          }
        />
      ) : null,
  },
  {
    caminho: "/relatorios",
    titulo: "Relatórios",
    icone: FileText,
    area: "projeto",
    renderizar: planejada(
      "Relatórios",
      "Relatórios do projeto para acompanhar e compartilhar.",
      [
        "Relatório mensal de resultados, com a análise da Monte.",
        "Exportação dos dados em planilha e PDF.",
      ],
    ),
  },

  {
    // Quem acessa a clinica: equipe Monte e gestor gerenciam; membro so visualiza o painel.
    caminho: "/acessos",
    titulo: "Acessos",
    icone: ShieldCheck,
    area: "projeto",
    visivel: (c) => c.equipeMonte || c.papelNoCliente === "gestor",
    renderizar: () => <PaginaDePessoas />,
  },

  // --- Configuracoes do projeto (equipe Monte e gestor) ---------------------
  {
    caminho: "/configuracoes/conexoes",
    titulo: "Conexões",
    icone: Settings2,
    area: "configuracoes",
    renderizar: (c) =>
      c.clienteId && c.cliente ? <PaginaDeConexoes clienteId={c.clienteId} cliente={c.cliente} /> : null,
  },
  {
    caminho: CAMINHO_DO_KOMMO,
    titulo: "Kommo CRM",
    icone: Settings2,
    area: "configuracoes",
    foraDoMenu: true,
    renderizar: (c) =>
      c.clienteId && c.cliente ? <PaginaDoKommo clienteId={c.clienteId} cliente={c.cliente} /> : null,
  },
  {
    caminho: CAMINHO_DO_META,
    titulo: "Meta Ads",
    icone: Settings2,
    area: "configuracoes",
    foraDoMenu: true,
    renderizar: (c) =>
      c.clienteId && c.cliente ? (
        <PaginaDoMeta clienteId={c.clienteId} cliente={c.cliente} equipeMonte={c.equipeMonte} />
      ) : null,
  },
  {
    caminho: CAMINHO_DO_GOOGLE,
    titulo: "Google Ads",
    icone: Settings2,
    area: "configuracoes",
    foraDoMenu: true,
    renderizar: (c) =>
      c.clienteId && c.cliente ? (
        <PaginaDoGoogle clienteId={c.clienteId} cliente={c.cliente} equipeMonte={c.equipeMonte} />
      ) : null,
  },
  {
    caminho: CAMINHO_DO_FUNIL_PROJETADO,
    titulo: "Funil Projetado",
    icone: Settings2,
    area: "configuracoes",
    renderizar: (c) =>
      c.clienteId && c.cliente ? <PaginaDeFunilProjetado clienteId={c.clienteId} cliente={c.cliente} /> : null,
  },
  {
    caminho: "/configuracoes/projeto",
    titulo: "Dados do projeto",
    icone: Settings2,
    area: "configuracoes",
    renderizar: (c) =>
      c.clienteId && c.cliente ? <PaginaDeDadosDoProjeto clienteId={c.clienteId} cliente={c.cliente} /> : null,
  },

  // --- Gestao (equipe Monte) -------------------------------------------------
  {
    caminho: "/perfil",
    titulo: "Meu perfil",
    icone: UserCog,
    area: "conta",
    foraDoMenu: true,
    renderizar: (c) => <PaginaDePerfil usuario={c.conta} cargoNoPainel={c.conta.cargoNoPainel} />,
  },
  { caminho: "/gestao/clientes", titulo: "Clientes", icone: Building2, area: "gestao", renderizar: () => <PaginaDeClientes /> },
  { caminho: "/gestao/usuarios", titulo: "Usuários", icone: UserCog, area: "gestao", renderizar: () => <PaginaDeUsuarios /> },
  {
    caminho: "/gestao/central",
    titulo: "Central",
    icone: Brain,
    area: "gestao",
    renderizar: () => <CentralAoVivo />,
  },
  {
    caminho: CAMINHO_DA_CONTA,
    titulo: "Conta",
    icone: Brain,
    area: "gestao",
    foraDoMenu: true,
    renderizar: () => <ContaAoVivo />,
  },

  // --- Configuracoes da Monte (equipe Monte, fora de qualquer clinica) -----
  {
    caminho: CAMINHO_DAS_CONEXOES_DA_MONTE,
    titulo: "Conexões",
    icone: KeyRound,
    area: "monte",
    renderizar: () => <PaginaDeConexoesDaMonte />,
  },
  {
    caminho: CAMINHO_DO_MONITORAMENTO,
    titulo: "Monitoramento de Contas",
    icone: Wallet,
    area: "monte",
    renderizar: () => <MonitoramentoComClinicas />,
  },
];

/** A pagina mostra a clinica de cada conta: o nome vem da lista de clientes da sessao. */
function MonitoramentoComClinicas() {
  const { clientes } = useClienteSelecionado();
  const nomes = new Map(clientes.map((cliente) => [cliente.id, cliente.nome]));
  return <PaginaDeMonitoramentoDeContas nomeDoCliente={(id) => nomes.get(id) ?? null} />;
}

const TITULO_DA_AREA: Readonly<Record<Area, string>> = {
  projeto: "Projeto",
  configuracoes: "Configurações do projeto",
  gestao: "Gestão",
  monte: "Configurações da Monte",
  conta: "Minha conta",
};

export function paginaPorCaminho(caminho: string): Pagina | undefined {
  return PAGINAS.find((pagina) => pagina.caminho === caminho);
}

export function podeVer(pagina: Pagina, contexto: ContextoDoMenu): boolean {
  return PODE_VER[pagina.area](contexto) && (pagina.visivel?.(contexto) ?? true);
}

export function tituloDaArea(pagina: Pagina): string {
  return TITULO_DA_AREA[pagina.area];
}

/**
 * Grupos da barra lateral para quem esta logado. As configuracoes ficam num
 * item recolhivel dentro de Projeto, como no `nav-main` do `sidebar-07`.
 */
export function gruposDoMenu(contexto: ContextoDoMenu): readonly GrupoDeNavegacao[] {
  const daArea = (area: Area) =>
    PAGINAS.filter((pagina) => pagina.area === area && !pagina.foraDoMenu && podeVer(pagina, contexto));
  const comoItem = (pagina: Pagina): ItemDeNavegacao => ({
    titulo: pagina.titulo,
    caminho: pagina.caminho,
    icone: pagina.icone,
  });

  const projeto: ItemDeNavegacao[] = daArea("projeto").map(comoItem);
  if (PODE_VER.configuracoes(contexto)) {
    projeto.push({
      titulo: "Configurações",
      icone: Settings2,
      subitens: daArea("configuracoes").map((pagina) => ({
        titulo: pagina.titulo,
        caminho: pagina.caminho,
      })),
    });
  }

  const gestao: ItemDeNavegacao[] = daArea("gestao").map(comoItem);
  if (PODE_VER.monte(contexto)) {
    gestao.push({
      titulo: TITULO_DA_AREA.monte,
      icone: Settings2,
      subitens: daArea("monte").map((pagina) => ({ titulo: pagina.titulo, caminho: pagina.caminho })),
    });
  }

  return [
    { titulo: TITULO_DA_AREA.projeto, itens: projeto },
    ...(PODE_VER.gestao(contexto) ? [{ titulo: TITULO_DA_AREA.gestao, itens: gestao }] : []),
  ];
}
